import { useEffect, useRef } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { actualizarFlete, actualizarDescuentoTotal } from '../../../service/AffariService';
import { useGlobalContext } from "../../../Config";
import { useOffline } from "../../../offline/OfflineContext";
import { actualizarDescuentoTotalCotizacionOffline, actualizarCotizacionOffline } from "../../../offline/offlineCotizacionService";



const ResumenCotizacion = ({ resumen = {}, setResumen, idCotizacion, notify, condicionEntrega, descuentoTotal = 0, onDescuentoTotalChange, onRefresh }) => {
    const lastFlete = useRef(resumen.flete ?? 0);
    const lastDescuentoTotal = useRef(Number(descuentoTotal || 0));
    const descuentoTotalDraft = useRef(Number(descuentoTotal || 0));
    const { isSack } = useGlobalContext();
    const { offlineMode } = useOffline();

    useEffect(() => {
        lastFlete.current = resumen.flete ?? 0;
    }, [resumen]);

    useEffect(() => {
        descuentoTotalDraft.current = Number(descuentoTotal || 0);
    }, [descuentoTotal]);

    useEffect(() => {
        const condicion = (condicionEntrega || 'RETIRO');
        if (condicion === 'RETIRO') {
            const currentFlete = resumen.flete ?? 0;
            if (currentFlete !== 0) {
                setResumen(prev => ({ ...prev, flete: 0 }));
                if (idCotizacion) {
                    (async () => {
                        try {
                            if (offlineMode) {
                                await actualizarCotizacionOffline(idCotizacion, { valorFlete: 0 });
                            } else {
                                await actualizarFlete(idCotizacion, 0);
                            }
                            lastFlete.current = 0;
                        } catch (error) {
                            if (notify) notify('error', 'Error actualizando flete', error.message || '');
                        }
                    })();
                }
            }
        }
    }, [condicionEntrega, idCotizacion, notify, offlineMode, resumen.flete, setResumen]);

    const handleDescuentoTotalChange = async (value) => {
        const nextValue = Math.min(100, Math.max(0, Number(value ?? 0)));
        if (!idCotizacion) return;
        if (nextValue === lastDescuentoTotal.current) return;

        try {
            if (offlineMode) {
                await actualizarDescuentoTotalCotizacionOffline(idCotizacion, nextValue);
            } else {
                await actualizarDescuentoTotal(idCotizacion, nextValue);
            }
            lastDescuentoTotal.current = nextValue;
            onDescuentoTotalChange?.(nextValue);
            await onRefresh?.(nextValue);
        } catch (error) {
            notify?.('error', 'Error actualizando descuento total', error.message || '');
        }
    };

    const handleFleteChange = async (value) => {
        const nextValue = value ?? 0;
        const minFlete = resumen.fleteMinimo ?? 0;

        if (nextValue < minFlete) {
            if (notify) {
                const minLabel = minFlete.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
                notify('warn', 'Flete mínimo', `El valor mínimo de flete es ${minLabel}`);
            }
            setResumen(prev => ({ ...prev, flete: lastFlete.current }));
            return;
        }

        if (!idCotizacion) { if (notify) notify('warn', 'Cotización', 'ID de cotización no disponible'); return; }
        if (nextValue === lastFlete.current) return;
        try {
            if (offlineMode) {
                await actualizarCotizacionOffline(idCotizacion, { valorFlete: nextValue });
            } else {
                await actualizarFlete(idCotizacion, nextValue);
            }
            lastFlete.current = nextValue;
            //if (notify) notify('success', 'Flete actualizado correctamente', 'valor');
        } catch (error) {
            if (notify) notify('error', 'Error actualizando flete', error.message || '');
        }
    };
    return (
        <div className="w-full flex justify-content-end">
            <div className="surface-0 p-3 text-sm w-full" style={{ maxWidth: '26rem' }}>
                {isSack && (
                    <>
                    
                        <div className="flex flex-column sm:flex-row sm:justify-content-between sm:align-items-center gap-2 mb-2">
                            <span className="text-500">Flete</span>
                            <InputNumber
                                value={resumen.flete ?? 0}
                                onValueChange={(e) => {
                                    const nextValue = e.value ?? 0;
                                    setResumen(prev => ({ ...prev, flete: nextValue }));
                                    handleFleteChange(nextValue);
                                }}
                                disabled={(condicionEntrega || 'RETIRO') === 'RETIRO'}
                                inputClassName="p-inputtext-sm text-right h-2rem w-full sm:w-8rem"
                                mode="currency"
                                currency="CLP"
                                locale="es-CL"
                                min={resumen.fleteMinimo ?? 0}
                            />
                        </div>
                    </>
                )}
                <div className="flex flex-column sm:flex-row sm:justify-content-between sm:align-items-center gap-2 mb-2">
                    <span className="text-500">Descuento total</span>
                    <InputNumber
                        value={Number(descuentoTotal || 0)}
                        onValueChange={(e) => {
                            const nextValue = e.value ?? 0;
                            descuentoTotalDraft.current = nextValue;
                            onDescuentoTotalChange?.(nextValue);
                        }}
                        onBlur={() => handleDescuentoTotalChange(descuentoTotalDraft.current)}
                        suffix=" %"
                        min={0}
                        max={100}
                        minFractionDigits={0}
                        maxFractionDigits={2}
                        inputClassName="p-inputtext-sm text-right h-2rem w-full sm:w-8rem"
                    />
                </div>
                <div className="flex justify-content-between mb-2">
                    <span className="text-500">Subtotal</span>
                    <span className="font-medium text-right">{resumen.subtotal ?? '$0'}</span>
                </div>
                <div className="flex justify-content-between mb-2">
                    <span className="text-500">Descuento</span>
                    <span className="font-medium text-right">{resumen.descuento ?? '$0'}</span>
                </div>
                <div className="flex justify-content-between mb-2">
                    <span className="text-500">Neto</span>
                    <span className="font-medium text-right">{resumen.neto ?? '$0'}</span>
                </div>
                <div className="flex justify-content-between mb-2">
                    <span className="text-500">IVA</span>
                    <span className="font-medium text-right">{resumen.iva ?? '$0'}</span>
                </div>
                <div className="flex justify-content-between font-bold text-lg mt-3 border-top-1 surface-border pt-2">
                    <span>Total</span>
                    <span>{resumen.total ?? '$0'}</span>
                </div>
            </div>
        </div>
    );
};

export default ResumenCotizacion;